<template>
  <div>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="600" persistent>
      <v-card>
      <v-card-title>Dodaj novog prodavača</v-card-title>

      <v-card-text>
        <v-form ref="form">
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="firstName"
                label="Ime"
                :rules="[rules.req(), rules.minLen(2, 'Ime mora imati najmanje 2 znaka')]"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="lastName"
                label="Prezime"
                :rules="[rules.req(), rules.minLen(2, 'Prezime mora imati najmanje 2 znaka')]"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
          </v-row>

          <v-text-field
            v-model="userName"
            label="Korisničko ime"
            :rules="[
              rules.req(),
              rules.username(),
              rules.minLen(4),
              v => !isExistingEmail || 'Korisničko ime već postoji'
            ]"
            @input="checkEmailAvailability"
            :suffix="`@${domain}.aikasa`"
            outlined
            dense
            hide-details="auto"
            class="mt-2"
          />

          <v-text-field
            v-model="password"
            label="Lozinka"
            type="password"
            :rules="[
              rules.req(),
              rules.validPass([firstName, lastName, userName])
            ]"
            outlined
            dense
            hint="Min 8 znakova, ne smije sadržavati ime/prezime/username"
            persistent-hint
            class="mt-2"
          />

          <v-text-field
            v-model="oib"
            label="OIB"
            :rules="[rules.req(), rules.oib()]"
            outlined
            dense
            hide-details="auto"
            class="mt-4"
          />

          <v-text-field
            v-model="pincode"
            label="PIN kod (4 cifre)"
            v-mask="'####'"
            :rules="[
              rules.req(),
              rules.minLen(4, 'PIN mora imati 4 cifre'),
              rules.maxLen(4, 'PIN mora imati 4 cifre'),
              v => !isExistingPin || 'PIN već postoji'
            ]"
            @input="checkPinAvailability"
            outlined
            dense
            hide-details="auto"
            class="mt-4"
          />

          <v-text-field
            v-model="designation"
            label="Oznaka operatera (opcionalno)"
            outlined
            dense
            hide-details="auto"
            class="mt-4"
          />
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close" :disabled="submitting">Odustani</v-btn>
        <v-btn color="primary" @click="submit" :loading="submitting">
          Dodaj prodavača
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <add-domain-dialog
    ref="addDomainDialog"
    @domainCreated="onDomainCreatedFromDialog"
  />
  </div>
</template>

<script>
import rules from '@/plugins/rules'
import { df, functions } from '@/plugins/firebase'
import { v4 as uuidv4 } from 'uuid'
import AddDomainDialog from './AddDomainDialog.vue'

export default {
  name: 'AddSellerDialog',
  components: {
    AddDomainDialog
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    companyId: {
      type: String,
      default: null
    },
    locationId: {
      type: String,
      default: null
    },
    domain: {
      type: String,
      default: ''
    }
  },
  inject: ['showMsgBox'],
  data () {
    return {
      firstName: '',
      lastName: '',
      userName: '',
      password: '',
      oib: '',
      pincode: '',
      designation: '',
      submitting: false,
      isExistingEmail: false,
      isExistingPin: false,
      rules
    }
  },
  watch: {
    async value (newVal) {
      if (!newVal) {
        this.reset()
      } else {
        // Kada se dialog otvara, provjeri domenu
        await this.checkAndCreateDomain()
      }
    }
  },
  methods: {
    async checkAndCreateDomain () {
      // Ako domena već postoji, ne radi ništa
      if (this.domain) {
        return
      }

      // Učitaj trenutnu firmu iz Firestore-a
      const companyDoc = await df.doc(`companies/${this.companyId}`).get()
      if (!companyDoc.exists) {
        console.error('Company not found')
        this.close()
        return
      }

      const company = { id: this.companyId, ...companyDoc.data() }

      // Ako firma već ima domenu, emituj event da se domena ažurira u parent-u
      if (company.domain) {
        this.$emit('domainCreated', company.domain)
        return
      }

      // Otvori AddDomainDialog
      this.$refs.addDomainDialog.open(company)
    },
    onDomainCreatedFromDialog (domain) {
      // Emituj event dalje prema parent-u
      this.$emit('domainCreated', domain)
    },
    async checkEmailAvailability () {
      if (this.userName.length > 3) {
        try {
          const email = `${this.userName}@${this.domain}.aikasa`
          const snapshot = await df
            .collection('users')
            .where('email', '==', email)
            .where('status', '==', 'OK')
            .get()

          this.isExistingEmail = !snapshot.empty
        } catch (error) {
          console.error('Error checking email availability:', error)
        }
      } else {
        this.isExistingEmail = false
      }
    },
    async checkPinAvailability () {
      if (this.pincode.length === 4) {
        try {
          const snapshot = await df
            .collection(`companies/${this.companyId}/users`)
            .where('user.pincode', '==', this.pincode)
            .where('user.status', '==', 'OK')
            .get()

          this.isExistingPin = !snapshot.empty
        } catch (error) {
          console.error('Error checking PIN availability:', error)
        }
      } else {
        this.isExistingPin = false
      }
    },
    validate () {
      return this.$refs.form.validate() &&
             !this.isExistingEmail &&
             !this.isExistingPin
    },
    async submit () {
      if (!this.validate()) {
        this.showMsgBox({
          text: 'Molimo ispunite sva polja pravilno',
          color: 'error'
        })
        return
      }

      this.submitting = true

      try {
        const newUserId = uuidv4()
        const userApi = functions.httpsCallable('userapi')

        const result = await userApi({
          action: {
            operation: 'set',
            entity: 'user',
            params: {
              user: {
                id: newUserId,
                name: this.firstName.toLowerCase(),
                surname: this.lastName.toLowerCase(),
                email: `${this.userName}@${this.domain}.aikasa`,
                password: this.password,
                password_confirmation: this.password,
                agree_terms_of_use: 'YES',
                designation: this.designation || '',
                pincode: this.pincode,
                roles: { PRODAVAČ: true },
                oib: this.oib
              },
              resources: {
                company_id: this.companyId,
                location_ids: [this.locationId],
                flag: 'add'
              }
            }
          }
        })

        // Provjeri response
        if (result.data && result.data.code === 200) {
          // Čekaj da se korisnik kreira
          await new Promise(resolve => setTimeout(resolve, 2000))

          this.showMsgBox({
            text: `Novi prodavač ${this.firstName} ${this.lastName} je uspješno dodan`,
            color: 'success'
          })

          this.$emit('sellerAdded', newUserId)
          this.close()
        } else {
          throw new Error(result.data?.error || result.data?.message || 'Greška pri dodavanju prodavača')
        }
      } catch (error) {
        console.error('Error adding seller:', error)
        this.showMsgBox({
          text: error.message || 'Dogodila se greška pri dodavanju prodavača',
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    },
    close () {
      this.$emit('input', false)
    },
    reset () {
      this.firstName = ''
      this.lastName = ''
      this.userName = ''
      this.password = ''
      this.oib = ''
      this.pincode = ''
      this.designation = ''
      this.isExistingEmail = false
      this.isExistingPin = false
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    }
  }
}
</script>
